/* @flow */
import * as React from "react";
import { connect } from "react-redux";
import Media from "react-media";
import { get } from "./common/fetch";
import { getByCurrency, getByPlatform } from "../configs/FillupConfigTypes";
import defaultConfig from "../configs/FillupConfig_default";
import Spinner from "./Spinners/BrandSpinner";

import type { State as GlobalState } from "../redux/reducer";
import type {
  BanksSettings,
  Billing,
  Deposit,
  Platform,
  Currency,
  Config
} from "../configs/FillupConfigTypes";
import type { FillupObject } from "../pages/fillup";

type UnrolledBilling = { ...Billing, min_amount: number | null };

export type UnrolledConfig = {
  platform: string,
  layout: string,
  banksSettings: BanksSettings | 0,
  billings: { [key: string]: UnrolledBilling },
  defaultBilling: string,
  defaultValue: number,
  deposits: Array<Deposit>,
  ptest: boolean
};

type UnrollProps = {
  layout: string,
  platform: Platform,
  currency: Currency,
  ptest: boolean
};

function unrollBilling(billing: Billing, props: UnrollProps): UnrolledBilling {
  if (billing.min_amount == null) {
    return {
      ...billing,
      min_amount: null,
      paymentImages: getByCurrency(props.currency, billing.paymentImages)
    };
  } else {
    return {
      ...billing,
      min_amount: getByCurrency(props.currency, billing.min_amount),
      paymentImages: getByCurrency(props.currency, billing.paymentImages)
    };
  }
}

function unrollConfig(config: Config, props: UnrollProps): UnrolledConfig {
  const allowedBillings = {};

  for (let type of Object.keys(config.billings)) {
    if (config.billings[type].allowedPlatforms.includes(props.platform)) {
      allowedBillings[type] = unrollBilling(config.billings[type], props);
    }
  }

  let defaultBilling;
  let defaultBank = null;
  let defaultDeposit = null;

  // Поле defaults — новее, мы используем его по-умолчанию

  if (config.defaults != null) {
    defaultBilling = config.defaults.billing;
    defaultBank = config.defaults.bank || null;
    defaultDeposit = config.defaults.amount || null;
  } else {
    defaultBilling = getByPlatform(props.platform, config.defaultBilling);
  }

  const billingTypes = Object.keys(allowedBillings);

  const billingDeposits = billingTypes.reduce((accumulated, billingType) => {
    const deposits = config.billings[billingType].deposits;
    let defaultDeposits;
    if (deposits) {
      defaultDeposits = getByCurrency(
        props.currency,
        getByPlatform(props.platform, deposits)
      );
    }

    return Object.assign({}, accumulated, { [billingType]: defaultDeposits });
  }, {});

  const deposits = getByCurrency(
    props.currency,
    getByPlatform(props.platform, config.deposits)
  );

  const getDefaultValue = billingType => {
    const billing = config.billings[billingType];

    if (
      billing &&
      billing.deposits &&
      billing.deposits.default &&
      billing.deposits.default[props.currency]
    ) {
      const values = billing.deposits.default[props.currency];
      const lastItem = values[values.length - 1];

      if (lastItem && lastItem.amount) {
        return lastItem.amount;
      }
    }

    return getByCurrency(props.currency, config.defaultValue);
  };

  let banksSettings =
    config.banksSettings === 0 ? 0 : { ...config.banksSettings };

  if (banksSettings !== 0) {
    // Обратная совместимость со старым форматом
    if (typeof banksSettings.forBilling === "string") {
      banksSettings = {
        [banksSettings.forBilling]: banksSettings
      };
    }

    banksSettings = Object.keys(banksSettings).reduce(
      (banksSettings, billing) => {
        let settings = banksSettings[billing];

        if (settings.banks != null) {
          let banks = settings.banks;
          banksSettings[billing] = {
            ...settings,
            banks: Array.isArray(banks)
              ? banks
              : getByCurrency(props.currency, banks)
          };
        } else if (settings.payment_points != null) {
          let payment_points = settings.payment_points;
          banksSettings[billing] = {
            payment_points: Array.isArray(payment_points)
              ? payment_points
              : getByCurrency(props.currency, payment_points)
          };
        } else if (settings.providers != null) {
          let providers = settings.providers;
          banksSettings[billing] = {
            ...settings,
            providers: Array.isArray(providers)
              ? providers
              : getByCurrency(props.currency, providers)
          };
        }

        return banksSettings;
      },
      banksSettings
    );
  }

  return {
    billingDeposits,
    deposits,
    defaultBilling,
    defaultBank,
    defaultDeposit,
    banksSettings,
    platform: props.platform,
    layout: props.layout,
    billings: allowedBillings,
    defaultValue: getByCurrency(props.currency, config.defaultValue),
    getDefaultValue,
    ptest: props.ptest
  };
}

type Props = {
  currency?: Currency,
  children: UnrolledConfig => React.Node
};

type State = {
  desktop: { [string]: Config } | null,
  mobile: { [string]: Config } | null
};

export class FillupConfiguration extends React.Component<Props, State> {
  state: State = {
    isFetched: false,
    desktop: null,
    mobile: null,
    ptest: false
  };

  renderChildren = (isMobile: boolean) => {
    if (isMobile) {
      if (!this.state.mobile) {
        return <Spinner />;
      }

      const mobileLayouts = Object.keys(this.state.mobile);
      const mobileLayout = mobileLayouts[0];

      const config = unrollConfig(this.state.mobile[mobileLayout], {
        currency: this.props.currency,
        platform: "mobile",
        layout: mobileLayout,
        ptest: this.state.ptest
      });

      return this.props.children(config);
    } else {
      if (!this.state.desktop) {
        return <Spinner />;
      }

      const desktopLayouts = Object.keys(this.state.desktop);
      const desktopLayout = desktopLayouts[0];

      const config = unrollConfig(this.state.desktop[desktopLayout], {
        currency: this.props.currency,
        platform: "desktop",
        layout: desktopLayout,
        ptest: this.state.ptest
      });

      return this.props.children(config);
    }
  };

  receiveConfigs = (configs: [{ [string]: Config }, { [string]: Config }]) => {
    const [desktop, mobile] = configs;

    this.setState({ desktop, mobile });
  };

  fetch() {
    const isPaymentTest = this.props.location.search.indexOf("ptest") > -1;
    get("/api/billings/full?locale=" + this.props.locale)
      .then(this.receiveConfigs)
      .catch(() => {
        console.warn("Running /fillup on default configuration");
        this.receiveConfigs(defaultConfig);
      })
      .then(() => this.setState({ isFetched: true, ptest: isPaymentTest }));
  }

  componentDidMount() {
    this.fetch();
    window.ptest = () => this.setState({ ptest: true });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.locale !== this.props.locale) {
      this.fetch();
    }
  }

  render() {
    if (!this.state.isFetched) {
      return <Spinner />;
    }

    return <Media query="(max-width: 959px)">{this.renderChildren}</Media>;
  }
}

const FillupConfigurationWithCurrency = connect((state: GlobalState) => ({
  currency: state.profile ? state.profile.currency : "USD"
}))(FillupConfiguration);

export default FillupConfigurationWithCurrency;

export function withConfiguration(
  Component: React.ComponentType<{ config: UnrolledConfig, currency: Currency }>
) {
  return function Configured(props) {
    return (
      <FillupConfigurationWithCurrency>
        {config => <Component {...props} config={config} />}
      </FillupConfigurationWithCurrency>
    );
  };
}

export function getPaymentBonus(config: UnrolledConfig, value: number): number {
  for (const deposit of config.deposits) {
    if (value >= Number(deposit.amount)) {
      return deposit.bonus;
    }
  }

  return 0;
}

export type Promo = {
  status: "OK" | "ERROR",
  real_money: 1 | 0,
  type_of_bonus: "percent" | "money",
  size_of_bonus: Array<{
    min_dep: number | null,
    max_dep: number | null,
    bonus: number
  }>,
  description: string
};

export function getPromoBonus(promo: Promo, value: number): number {
  for (const deposit of promo.size_of_bonus) {
    let min_dep = deposit.min_dep === null ? 0 : deposit.min_dep;
    let max_dep = deposit.max_dep === null ? Infinity : deposit.max_dep;

    if (value >= min_dep && value < max_dep) {
      return deposit.bonus;
    }
  }

  return 0;
}

export function getBillingBonus(
  config: UnrolledConfig,
  billingType: string
): number {
  const billing = config.billings[billingType];

  if (billing) {
    return billing.bonus;
  }

  return 0;
}

// Второе поколение хелперов основано на единственом параметре - FillupObject

export function getRegularBonus(fillup: FillupObject): number {
  return (
    getPaymentBonus(fillup.config, fillup.deposit) +
    getBillingBonus(fillup.config, fillup.billingType)
  );
}

export function getAbsoluteRegularBonus(fillup: FillupObject): number {
  return (fillup.deposit * getRegularBonus(fillup)) / 100;
}

export function getAbsolutePromoBonus(fillup: FillupObject): number {
  if (fillup.promo != null) {
    switch (fillup.promo.type_of_bonus) {
      case "money":
        return getPromoBonus(fillup.promo, fillup.deposit);
      case "percent":
        return (
          (fillup.deposit * getPromoBonus(fillup.promo, fillup.deposit)) / 100
        );
      default:
        throw new TypeError("Unknown promo bonus type");
    }
  } else {
    return 0;
  }
}

export function getBonus(fillup: FillupObject): number {
  return fillup.promo != null
    ? getPromoBonus(fillup.promo, fillup.deposit)
    : getRegularBonus(fillup);
}

export function getAbsoluteBonus(fillup: FillupObject): number {
  return fillup.promo != null
    ? getAbsolutePromoBonus(fillup)
    : getAbsoluteRegularBonus(fillup);
}



// WEBPACK FOOTER //
// ./src/components/FillupConfiguration.js